import { appointmentShowOptions, appointmentUpdateMutation, appointmentIndexQueryKey, appointmentShowQueryKey, appointmentCalendarQueryKey } from '$api-client/@tanstack/react-query.gen'
import { useCalendar } from '$features/calendar/hooks/use-calendar'
import {
  NavigableModal,
  Text,
} from '$components'
import {
  ArrowLongRightIcon,
  EyeIcon,
  LoaderIcon,
} from '$components'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useParams } from 'react-router'
import { ServicePill } from '$components'
import { EntityLink } from '$components'
import { Router } from '$utils/router'
import { ClientRoutePath } from '$features/clients/routes/protected'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { OptionsDropdown } from '$components'
import { AppointmentStatusPill } from '$components'
import { getCurrencySymbol } from '$utils/currency'
import { AppointmentRoutePath } from '$features/appointments/routes/protected'
import { AppointmentStatus } from '$api-client/types.gen'
import { ToastHelper } from '$components'
import { QueryErrorHelper } from '$utils/query'

export function ViewModal() {
  const { navigate, viewType } = useCalendar()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    ...appointmentShowOptions({
      path: {
        id: Number(id),
      },
    }),
  })

  const { mutate: updateAppointment } = useMutation({
    ...appointmentUpdateMutation(),
    onSuccess: () => {
      ToastHelper.success('Appointment status updated successfully.')
      queryClient.invalidateQueries({
        queryKey: appointmentIndexQueryKey(),
      })

      queryClient.invalidateQueries({
        queryKey: appointmentShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })

      queryClient.invalidateQueries({
        queryKey: appointmentCalendarQueryKey(),
      })
    },
    onError: (error) => {
      const validation = QueryErrorHelper.getQueryErrorByCode(error, 'unavailable_slot')
      ToastHelper.error(validation ? validation.message : 'Failed to update appointment status.')
    },
  })

  const appointment = data?.data

  const handleStatusChange = (newStatus: AppointmentStatus) => {
    updateAppointment({
      path: {
        id: Number(id),
      },
      body: {
        status: newStatus,
      },
    })
  }

  const onClose = () => navigate(`/calendar/${viewType}/`)

  return (
    <NavigableModal onClose={onClose}>
      <NavigableModal.Header>
        <div className="flex items-center justify-between gap-stack-tight mr-stack-dense">
          <Text as="h2">
            Appointment
            <span className="text-fg-subtle inline font-normal text-body">
              &nbsp;#{id}
            </span>
          </Text>

          <OptionsDropdown options={[
            {
              id: 'view',
              value: 'View Details',
              icon: EyeIcon,
              onClick: () => navigate(Router.getRoute(AppointmentRoutePath.AppointmentsView, { id: appointment!.id })),
            },
          ]}
          />
        </div>
      </NavigableModal.Header>

      <NavigableModal.Content>
        {/* Loading */}
        { isLoading && (
          <div className="flex flex-col items-center justify-center h-96">
            <LoaderIcon className="size-6 text-fg-subtle" />
          </div>
        )}

        {/* Not Found */}
        { !appointment && !isLoading && (
          <div className="flex flex-col items-center justify-center h-96">
            <Text className="text-fg-subtle mb-stack-dense">Appointment not found</Text>
          </div>
        )}

        {/* Content */}
        { appointment && (
          <div className="flex flex-col gap-stack-roomy h-full">
            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Client</Text>
              <EntityLink
                path={Router.getRoute(ClientRoutePath.ClientsView, { id: appointment.client.id.toString() })}
              >
                {`${appointment.client.first_name} ${appointment.client.last_name}`}
              </EntityLink>
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Pet</Text>
              <EntityLink
                path={Router.getRoute(PetRoutePaths.PetsView, { id: appointment.pet.id.toString() })}
              >
                {appointment.pet.name}
              </EntityLink>
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Status</Text>
              <AppointmentStatusPill
                status={appointment.status}
                onStatusChange={handleStatusChange}
              />
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Services</Text>
              <div className="flex items-center gap-stack-dense">
                {appointment.services.map(s => (
                  <ServicePill
                    key={s.id}
                    service={{
                      id: s.service.id,
                      values: {
                        id: s.service_values.id,
                        is_latest: s.service_values.is_latest,
                        name: s.service_values.name,
                      },
                      colour: s.service.colour,
                    }}
                  />
                ))}
              </div>
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Date & Time</Text>
              <div className="flex items-center gap-stack-tight">
                <Text type="s">
                  {dayjs.utc(appointment.start_at).format('DD MMM YYYY, HH:mm A')}
                </Text>

                <ArrowLongRightIcon className="size-4 text-fg-subtle" />

                <Text type="s">
                  {dayjs.utc(appointment.finish_at).format('HH:mm A')}
                </Text>

                <Text type="s">
                  ({dayjs.utc(appointment.finish_at).diff(appointment.start_at, 'minutes')} minutes)
                </Text>
              </div>
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Groomer</Text>
              <Text type="s">{appointment.groomer.first_name} {appointment.groomer.last_name}</Text>
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Price</Text>
              <Text type="s">{getCurrencySymbol(appointment.currency)}{(appointment.price_in_cents / 100).toFixed(2)}</Text>
            </div>

            <div>
              <Text
                type="s"
                className="text-fg-subtle mb-stack-dense"
              >Internal Notes</Text>
              <Text type="s">{appointment.notes || 'No notes'}</Text>
            </div>
          </div>
        )}
      </NavigableModal.Content>
    </NavigableModal>
  )
}
